import React from 'react';
import { css } from '@emotion/react';
import { DateCalendar, PickersDay } from '@mui/x-date-pickers';
import { format } from 'date-fns';

import { colors, typography } from '@/styles';
import { TOKENS } from '../../blocks';

// Calendar cells are calendar dates, so they go to and from 'YYYY-MM-DD' in the browser's own
// zone — no instant is involved until the schedule is written.
export const toKey = (date) => format(date, 'yyyy-MM-dd');

// Undefined for a missing key: the dialog stays mounted while closed, with no day to take today from.
export const fromKey = (key) => {
    if (!key) return undefined;

    const [year, month, day] = key.split('-').map(Number);
    return new Date(year, month - 1, day);
};

// MUI X renders @mui/material, which reads the v5 default palette, so a picked day would be
// Material blue. Onward green instead, scoped to these cells.
const DAY = {
    width: 30,
    height: 30,
    margin: '0 2px',
    fontSize: '0.75rem',
    '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
        backgroundColor: colors.greens.primary,
    },
};

// Scaled down from MUI's 320px default to sit in a card under the dropdown: 30px days and a
// shorter header, so six weeks fit without the grid jumping height between months.
const CALENDAR = {
    margin: 0,
    width: 254,
    height: 'auto',
    maxHeight: 'none',
    '& .MuiPickersCalendarHeader-root': { margin: 0, padding: '0 0 0 8px', minHeight: 36, maxHeight: 36 },
    '& .MuiPickersCalendarHeader-label': { fontSize: '0.8125rem', fontWeight: 600 },
    '& .MuiDayCalendar-weekDayLabel': { width: 30, height: 24, margin: '0 2px', fontSize: '0.6875rem' },
    // The blanks before the 1st and after the last day are a separate element that the day slot's
    // `sx` never reaches, so they kept MUI's 36px and stretched the first and last weeks.
    '& .MuiPickersDay-hiddenDaySpacingFiller': { width: 30, height: 30, margin: '0 2px' },
    '& .MuiDayCalendar-slideTransition': { minHeight: 6 * 34 },
    '& .MuiDayCalendar-monthContainer': { position: 'relative' },
};

// The design Card's border and corners, drawn here rather than by wrapping in it: its DesignScope
// preflight resets margin and padding on everything inside, which took the calendar's layout apart.
const CARD = css`
    display: inline-block;
    padding: 10px 12px;
    border: 1px solid ${TOKENS.border};
    border-radius: 8px;
    background: #fff;
`;

const Caption = ({ children }) => (
    <div
        css={css`
            ${typography.sansSerif}
            font-size: 12px;
            font-weight: 600;
            color: #2b2b2b;
            margin-bottom: 4px;
        `}
    >
        {children}
    </div>
);

// A day cell that reads as picked when the caller says so. The calendar's own `value` stays null,
// so it never holds a selection of its own and every click comes through as a toggle. aria-selected
// too: the calendar sets it from that null value, so screen readers heard every day as unpicked.
const PickedDay = ({ isPicked, ...props }) => {
    const picked = !props.outsideCurrentMonth && isPicked(toKey(props.day));

    return <PickersDay {...props} selected={picked} aria-selected={picked} sx={DAY} />;
};

// Several dates at once, which the design package's DatePicker can't do.
//
//   isPicked(key)   whether a cell is lit — a set of dates, or a set of days of the month
//   onToggle(key)   a cell was clicked
//   caption         what the operator is picking
export default function MiniCalendar({ isPicked, onToggle, minKey, referenceKey, caption, error }) {
    return (
        <div>
            <div css={CARD}>
                {caption ? <Caption>{caption}</Caption> : null}
                <DateCalendar
                    value={null}
                    onChange={(date) => {
                        if (date) onToggle(toKey(date));
                    }}
                    minDate={minKey ? fromKey(minKey) : undefined}
                    referenceDate={referenceKey ? fromKey(referenceKey) : undefined}
                    slots={{ day: PickedDay }}
                    slotProps={{ day: { isPicked } }}
                    sx={CALENDAR}
                />
            </div>
            {error ? (
                <div
                    css={css`
                        ${typography.sansSerif}
                        font-size: 11px;
                        color: ${colors.reds.primary};
                        margin-top: 6px;
                    `}
                >
                    {error}
                </div>
            ) : null}
        </div>
    );
}
